import React, { useEffect, useState } from "react";
import { useNavigate, NavLink, Outlet, Link } from "react-router-dom";
import { auleloApi, auleloAuth } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { LogOut, ShieldCheck, Newspaper, Globe2, Plane, Settings as SettingsIcon, Mic2, Brain, Radar } from "lucide-react";
import { toast } from "sonner";

export default function AuleloShell() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!auleloAuth.getToken()) {
      nav("/Kittanna", { replace: true });
      return;
    }
    auleloApi.get("/me").then(({ data }) => {
      if (data.first_login) {
        nav("/aulelo/first-login", { replace: true });
      } else {
        setMe(data);
      }
    }).catch((err) => {
      if (err?.response?.status === 401) {
        auleloAuth.clear();
        toast.error("Session expired");
        nav("/Kittanna", { replace: true });
      }
    });
  }, [nav]);

  const logout = () => {
    auleloAuth.clear();
    toast.success("Locked");
    nav("/Kittanna", { replace: true });
  };

  if (!me) return null;

  return (
    <div className="min-h-screen bg-black text-zinc-200">
      {/* CRT-ish scanlines */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: "repeating-linear-gradient(0deg, rgba(0,255,140,0.02) 0px, rgba(0,255,140,0.02) 1px, transparent 1px, transparent 3px)"
      }} />

      <header className="relative z-10 border-b border-emerald-700/30 bg-black/70 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="font-mono font-black text-emerald-200 leading-none tracking-tight text-lg" data-testid="aulelo-agent-name">{me.agent_name}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 leading-none mt-1">classified channel</div>
            </div>
          </div>

          <nav className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
            {[
              { to: "/aulelo/console", label: "Briefing", icon: Newspaper, end: true, testid: "aulelo-nav-briefing" },
              { to: "/aulelo/console/world", label: "World", icon: Globe2, testid: "aulelo-nav-world" },
              { to: "/aulelo/console/tours", label: "Tours", icon: Plane, testid: "aulelo-nav-tours" },
              { to: "/aulelo/console/vasa", label: "Vasa", icon: Brain, testid: "aulelo-nav-vasa" },
              { to: "/aulelo/console/watchlist", label: "Watchlist", icon: Radar, testid: "aulelo-nav-watchlist" },
              { to: "/aulelo/console/podcast", label: "Podcast", icon: Mic2, testid: "aulelo-nav-podcast" },
              { to: "/aulelo/console/settings", label: "Vault", icon: SettingsIcon, testid: "aulelo-nav-settings" },
            ].map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border flex items-center gap-2 whitespace-nowrap ${
                    isActive
                      ? "border-emerald-500 text-emerald-200 bg-emerald-500/10"
                      : "border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-emerald-700/40"
                  }`
                }
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/admin" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 px-2 py-1 border border-zinc-800">← Lingent</Link>
            <Button
              onClick={logout}
              data-testid="aulelo-logout-btn"
              variant="outline"
              className="rounded-none border-emerald-700/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 font-mono text-[10px] uppercase tracking-widest"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Lock
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-[1500px] mx-auto px-6 py-8">
        <Outlet context={{ me }} />
      </main>
    </div>
  );
}
